import * as Sentry from '@sentry/react'

const IS_DEV = import.meta.env.DEV
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN

type SentryRouter = Parameters<typeof Sentry.tanstackRouterBrowserTracingIntegration>[0]

let initialized = false

/**
 * Initialize Sentry. Must be called with the TanStack Router instance so that
 * navigation/pageload transactions are named by the parameterized route
 * (e.g. `/catalog/products/$selectedItem`) instead of the raw URL — without
 * this, every product/inventory page becomes its own high-cardinality
 * transaction and performance data is unusable.
 */
export function initSentry(router: SentryRouter): void {
  if (initialized) return
  initialized = true

  if (!SENTRY_DSN) {
    console.info('[sentry] DSN not configured, skipping initialization')
    return
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.VITE_ENV ?? (IS_DEV ? 'development' : 'production'),
    release: import.meta.env.VITE_SENTRY_RELEASE ?? undefined,

    tracesSampleRate: IS_DEV ? 1.0 : 0.2,

    // Browser profiling (flame charts for slow interactions). Sampled relative to
    // traced transactions. Requires the document to be served with the
    // `Document-Policy: js-profiling` header — set on the vite dev server and
    // must be set on the prod host/CDN too, otherwise this is a silent no-op.
    profilesSampleRate: IS_DEV ? 1.0 : 0.1,

    // Connect frontend traces to the backend (rootpoint-core) so a single trace
    // spans the browser action → API request → DB. The API's CORS now allows the
    // `sentry-trace` / `baggage` headers (see backend cors allowedHeaders).
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/api(v2)?(-dev)?\.rootpoint\.io/,
      /^https:\/\/live-dev-app\.rootpoint\.io/,
    ],

    // Structured logs (self-hosted Sentry surfaces these in the Logs view).
    enableLogs: true,

    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    replaysSessionSampleRate: IS_DEV ? 0 : 0.1,
    replaysOnErrorSampleRate: 1.0,

    beforeSend(event) {
      if (event.request?.headers) {
        const sensitiveHeaders = ['authorization', 'cookie', 'x-api-key', 'x-auth-token']
        for (const header of sensitiveHeaders) {
          if (event.request.headers[header]) {
            event.request.headers[header] = '[Filtered]'
          }
        }
      }
      return event
    },

    ignoreErrors: [
      /ECONNRESET/,
      /504 Gateway Time-out/,
      'Network Error',
      'Request aborted',
      // TanStack Query cancels in-flight fetches on unmount/key change — that's
      // control flow, not an error (WEB-5W/6B were pure noise).
      'CancelledError',
      // The auth identity guard's deliberate logout when the browser-wide
      // refresh cookie belongs to another account (WEB-6P) — working as
      // designed, not a defect.
      'Signed-in account changed in another tab',
    ],

    sendDefaultPii: false,
  })
}
